var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var RankConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './sortColumn'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { rankConfigSchemaValidation } from '../validations.generated';
export var RankType;
(function (RankType) {
    RankType["RANK"] = "rank";
    RankType["DENSE_RANK"] = "dense_rank";
    RankType["PERCENT_RANK"] = "percent_rank";
})(RankType || (RankType = {}));
export const rankConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:RankConfig',
    type: 'object',
    properties: {
        type: { type: 'string', enum: Object.values(RankType) },
        alias: { type: 'string', minLength: 1, maxLength: 150 },
        groupColumns: {
            type: 'array',
            items: { type: 'string', minLength: 1, maxLength: 150 },
            uniqueItems: true,
            maxItems: 1000
        },
        orderColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:SortColumn' },
            uniqueItemProperties: ['name'],
            minItems: 1,
            maxItems: 1000
        }
    },
    required: ['type', 'alias', 'groupColumns', 'orderColumns']
};
let RankConfig = RankConfig_1 = class RankConfig extends ToolConfig {
    constructor(type, alias, groupColumns, orderColumns, customFormats) {
        super(ToolType.RANK, customFormats);
        this.type = type;
        this.alias = alias;
        this.groupColumns = groupColumns;
        this.orderColumns = orderColumns;
        JSON.validate(this, RankConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return RankConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return RankConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return RankConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return RankConfig_1.outs;
    }
    static get in() {
        return RankConfig_1.ins[0];
    }
    static get out() {
        return RankConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.RANK.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.ORGANIZE], this.ins, this.outs, ['row number'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new RankConfig_1(baseVals.type, baseVals.alias, baseVals.groupColumns, parameters.getAppliedVals(this.orderColumns), this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            type: this.type,
            alias: this.alias,
            groupColumns: this.groupColumns,
            orderColumns: this.orderColumns.map((col) => col.getBaseValues(parameters))
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const baseVals = this.getBaseValues(iConfigHelper.getParameters());
            // don't copy group columns as getGroupByDependents does it
            const grpColSet = new Set();
            const orderColsSet = new Set();
            baseVals.groupColumns.forEach((grpCol) => grpColSet.add(grpCol));
            baseVals.orderColumns.forEach((col) => orderColsSet.add(col.name));
            source.forEach((colMet) => {
                if (!grpColSet.has(colMet.name))
                    exp.copy(this.in, colMet, false, orderColsSet.has(colMet.name));
            });
            const dependents = [];
            orderColsSet.forEach((col) => dependents.push(this.getDependent(exp, this.in, col)));
            this.getGroupByDependents(exp, this.in, baseVals.groupColumns, dependents).forEach((grpByDep) => dependents.push(grpByDep));
            exp.makeNew(new Column(baseVals.alias, 'number'), dependents, true);
            exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const inputs = new Set(this.groupColumns);
        baseVals.orderColumns.forEach((c) => inputs.add(c.name));
        const sourceValidationResult = validateSourceWithDetail(source, inputs, [baseVals.alias]);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
RankConfig = RankConfig_1 = __decorate([
    JsonSchemaV2(rankConfigSchema, rankConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.RANK)
], RankConfig);
export { RankConfig };
//# sourceMappingURL=rankConfig.js.map